/**
 * PLAYER module — capsule-vs-AABB collision against meshes WORLD tags with
 * `userData.solid === true` (the contract's collidable registration; PLAYER
 * scans `ctx.scene` and snapshots world-space AABBs). Owned by PLAYER.
 */
import { Box3 } from 'three'
import type { Object3D, Scene } from 'three'

import { COLLISION_ITERATIONS } from './constants'

const EPSILON_SQ = 1e-9

/** Snapshot world-space AABBs for every solid-tagged object in the scene. */
export const collectSolidBoxes = (scene: Scene): readonly Box3[] => {
  const boxes: Box3[] = []
  scene.traverse((object: Object3D) => {
    if (object.userData.solid !== true) return
    const box = new Box3().setFromObject(object)
    if (!box.isEmpty()) {
      boxes.push(box)
    }
  })
  return boxes
}

export interface HorizontalPosition {
  readonly x: number
  readonly z: number
}

interface CapsuleSpec {
  readonly feetY: number
  readonly headY: number
  readonly radius: number
}

/**
 * Resolve a vertical player capsule against AABBs, pushing out horizontally
 * (ground is flat; Y stays at eye height). Returns the corrected XZ position
 * as a new object — inputs are never mutated.
 */
export const resolveCapsuleAgainstBoxes = (
  position: HorizontalPosition,
  capsule: CapsuleSpec,
  boxes: readonly Box3[]
): HorizontalPosition => {
  let { x, z } = position
  const { feetY, headY, radius } = capsule
  const radiusSq = radius * radius

  for (let iteration = 0; iteration < COLLISION_ITERATIONS; iteration += 1) {
    let corrected = false

    for (const box of boxes) {
      // No vertical overlap with the capsule's span — floors/ceilings pass.
      if (box.max.y <= feetY || box.min.y >= headY) continue

      const closestX = Math.min(Math.max(x, box.min.x), box.max.x)
      const closestZ = Math.min(Math.max(z, box.min.z), box.max.z)
      const dx = x - closestX
      const dz = z - closestZ
      const distSq = dx * dx + dz * dz
      if (distSq >= radiusSq) continue

      corrected = true
      if (distSq > EPSILON_SQ) {
        const dist = Math.sqrt(distSq)
        const push = radius - dist
        x += (dx / dist) * push
        z += (dz / dist) * push
      } else {
        // Capsule axis inside the box footprint: exit via the nearest face.
        const toMinX = x - (box.min.x - radius)
        const toMaxX = box.max.x + radius - x
        const toMinZ = z - (box.min.z - radius)
        const toMaxZ = box.max.z + radius - z
        const smallest = Math.min(toMinX, toMaxX, toMinZ, toMaxZ)
        if (smallest === toMinX) x = box.min.x - radius
        else if (smallest === toMaxX) x = box.max.x + radius
        else if (smallest === toMinZ) z = box.min.z - radius
        else z = box.max.z + radius
      }
    }

    if (!corrected) break
  }

  return { x, z }
}
